/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import { useUser } from '#hooks/UNSAFE_useUser';
import { classNames } from '#utils/UNSAFE_classNames';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { styles } from './themes/MeterBarStyles.css';
import { VisThresholdsColors, getVisThresholdColor } from '#utils/UNSAFE_meterUtils';

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'class'>;
type ThresholdBarProps = IntrinsicProps & {
  color?: ColorProps['color'] | VisThresholdsColors;
  length: string;
  orientation: 'horizontal' | 'vertical';
};

export function ThresholdBar({ color, length, orientation, ...otherProps }: ThresholdBarProps) {
  const { direction } = useUser();
  const isHoriz = orientation === 'horizontal';
  const isRtl = direction === 'rtl';

  return (
    <div
      class={classNames([
        styles.thresholdBarBaseStyle,
        isHoriz
          ? isRtl
            ? styles.thresholdBarHorizontalBarTrackSectionRtlStyle
            : styles.thresholdBarHorizontalBarTrackSectionLtrStyle
          : styles.thresholdBarVerticalBarTrackSectionStyle,
        otherProps.class
      ])}
      style={{
        width: isHoriz ? length : '100%',
        height: isHoriz ? '100%' : length,
        backgroundColor: getVisThresholdColor(color)
      }}></div>
  );
}
